import React, {
  useState,
  useMemo,
  useRef,
  useCallback,
  useEffect,
} from 'react';
import { useTranslation } from 'react-i18next';

import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  Row,
  useReactTable,
} from '@tanstack/react-table';

// Types
import {
  ExternalCompaniesSelectionType,
  columnNames,
  getExternalCompaniesColumnNames,
  ExternalProps,
  cellExternalCompaniesTypes,
} from './types';
import { ImperativeRefInput } from '../../../../inputs/DropDown/DropDownInput/types';

import { OptionSelected } from '../../../../inputs/DropDown/DropDownList/types';

// Styled Components
import {
  Table,
  Tbody,
  Thead,
  Tr,
} from '../../summaryTables/SummaryTablesStyles';
import {
  TdStyled,
  InputNumber,
  ContainerFilters,
  Container,
  StyledTableContainer,
  Img,
  SubContainer,
  ContainerInputCompanies,
  SubContainerFilters,
  ThStyled,
  TRBody,
} from './styles';

// Components
import { MainButton } from '../../../../inputs/buttons/MainButton';
import { Typography } from '../../../Typography';
import DropDownInput from '../../../../inputs/DropDown/DropDownInput';

// Icons
import RemoveIcon from '../../../../../assets/icons/remove-file-icon.svg';

// Mocks
import { onChangeValue } from '../../../../inputs/DropDown/DropDownSelectGroup/types';
import {
  compressSessionStorage,
  decompressSessionStorage,
} from '../../../../../utils/sessionStorage';
import { externalCompaniesNaming } from './constants';

export const ExternalCompaniesSelectionTable = ({
  defaultValues,
  setValue,
  getValues,
  setDefaultExternals,
  externalCompanies,
  ...props
}: ExternalProps) => {
  const { t } = useTranslation('forms');

  // Dynamic column names with translations
  const translatedColumnNames = useMemo(
    () => getExternalCompaniesColumnNames(t),
    [t]
  );

  const [data, setData] = useState<ExternalCompaniesSelectionType[]>(
    defaultValues ?? []
  );
  const [selectedOption, setSelectedOption] = useState<OptionSelected>();
  /**
   * Solo empresas ACTIVAS. Las dadas de baja desde Gestión se siguen viendo en
   * las actuaciones que ya las usan (el nombre se resuelve por id, no por este
   * listado), pero no deben poder seleccionarse en actuaciones nuevas.
   *
   * `!== false` y no `=== true`: si algún consumidor todavía no pide `isActive`
   * en su query, el campo llega `undefined` y la empresa se sigue ofreciendo,
   * en vez de vaciar el desplegable en silencio.
   */
  const companies = externalCompanies
    .filter((company) => company.isActive !== false)
    .map((company) => ({
      value: company.id,
      label: company.companyName,
    }));

  const companyRef = useRef<ImperativeRefInput>(null);
  const operatorsNumberRef = useRef<HTMLInputElement>(null);

  const companiesColumnHelper =
    createColumnHelper<ExternalCompaniesSelectionType>();

  const columns = useMemo(
    () => [
      companiesColumnHelper.accessor((row) => row.company, {
        id: translatedColumnNames.company.id,
        header: () => translatedColumnNames.company.label,
        cell: (info) => info.getValue(),
      }),

      companiesColumnHelper.accessor((row) => row.operatorsNumber, {
        id: translatedColumnNames.operatorsNumber.id,
        header: () => translatedColumnNames.operatorsNumber.label,
      }),
      companiesColumnHelper.accessor((row) => row.remove, {
        id: translatedColumnNames.remove.id,
        header: () => (
          <Typography colorText="white" size="S">
            -
          </Typography>
        ),
        cell: ({ row }) => (
          <Img src={RemoveIcon} onClick={() => removeRow(row)} />
        ),
      }),
    ],
    [translatedColumnNames]
  );

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });

  const addData = useCallback(() => {
    const newRow: ExternalCompaniesSelectionType = {
      company: selectedOption?.label,
      operatorsNumber: operatorsNumberRef.current?.value,
      id: selectedOption?.value,
    };
    if (
      selectedOption !== undefined &&
      operatorsNumberRef.current?.value !== '' &&
      parseInt(operatorsNumberRef.current?.value as string) > 0 &&
      data.some((item) => item.company === selectedOption?.label)
    ) {
      const previousValues = getValues('externals');
      const idToChange =
        previousValues.findIndex(
          (item) => item.company === selectedOption.label
        ) ?? {};

      previousValues[idToChange].operatorsNumber =
        operatorsNumberRef.current?.value;
      setValue('externals', previousValues);
      setData(previousValues);
    }

    if (data.some((company) => company.company === newRow.company)) {
      const previousValues = getValues('externals');
      const idToChange =
        previousValues.findIndex((item) => item.company === newRow.company) ??
        {};

      previousValues[idToChange]?.operatorsNumber &&
        (previousValues[idToChange].operatorsNumber = newRow?.operatorsNumber);
      setValue('externals', previousValues);
      setData(previousValues);
      return;
    }
    if (
      selectedOption !== undefined &&
      operatorsNumberRef.current?.value !== '' &&
      parseInt(operatorsNumberRef.current?.value as string) > 0 &&
      !data.some((item) => item.company === selectedOption?.label)
    ) {
      setData((prevData) => {
        const newData = [...prevData, newRow];
        return newData;
      });
      setValue('externals', [...data, newRow]);

      if (companyRef) companyRef.current?.clearSelected();
      if (operatorsNumberRef.current) operatorsNumberRef.current.value = '';
      const currentPath = window.location.pathname.split('/');

      if (currentPath.some((item) => item === 'actions-validation')) {
        const previousWorksToAdd = sessionStorage.getItem(
          externalCompaniesNaming.EXTERNAL_WORKS_TO_ADD
        );

        let previousWorkToAddParsed;

        if (previousWorksToAdd) {
          previousWorkToAddParsed = JSON.parse(
            decompressSessionStorage(previousWorksToAdd as string)
          );
        }

        const itemsToAdd = previousWorkToAddParsed
          ? [newRow, ...previousWorkToAddParsed]
          : [newRow];

        sessionStorage.setItem(
          externalCompaniesNaming.EXTERNAL_WORKS_TO_ADD,
          compressSessionStorage(JSON.stringify(itemsToAdd))
        );
      }
    }
  }, [selectedOption, data]);

  const removeRow = useCallback((row: Row<ExternalCompaniesSelectionType>) => {
    setData((prevData) => {
      const newData = [...prevData];
      const dataFilter = newData.filter(
        (company) => company.company !== row.original.company
      );
      setValue(`externals`, dataFilter);
      return dataFilter;
    });
    if (row.original.workId) {
      const previousWorksToDelete = sessionStorage.getItem(
        externalCompaniesNaming.EXTERNAL_WORKS_TO_DELETE
      );

      let previousWorksToDeleteParsed;

      if (previousWorksToDelete) {
        previousWorksToDeleteParsed = JSON.parse(
          decompressSessionStorage(previousWorksToDelete as string)
        );
      }

      const idsToDelete = previousWorksToDeleteParsed
        ? [row.original.workId, ...previousWorksToDeleteParsed]
        : [row.original.workId];

      sessionStorage.setItem(
        externalCompaniesNaming.EXTERNAL_WORKS_TO_DELETE,
        compressSessionStorage(JSON.stringify(idsToDelete))
      );
    }
  }, []);

  const handleSelectedCompany = useCallback((option: OptionSelected) => {
    setSelectedOption(option);
  }, []);

  useEffect(() => {
    setDefaultExternals(data);
  }, [data]);

  return (
    <Container {...props}>
      <SubContainer>
        <Typography size="L" variant="semiBold">
          {t('externalCompanies.addWork')}
        </Typography>
        <ContainerFilters>
          <ContainerInputCompanies>
            {/*
              Desde 2026-07 NO hay opción "Nova empresa": el alta del catálogo
              vive en Gestió > Empreses externes, restringida a
              super-admin/coex/capataz. Aquí solo se selecciona.
            */}
            <DropDownInput
              ref={companyRef}
              inputSize="M"
              typeDropDown="Default"
              placeholder=""
              labelText={t('company.name')}
              options={companies}
              onChangeSelected={handleSelectedCompany as onChangeValue}
            />
          </ContainerInputCompanies>
          <SubContainerFilters>
            {/* <InputText
              ref={workRef}
              typeInput="normal"
              label={t('workOrder.work')}
              placeholder={t('common:writeHere')}
            /> */}

            <InputNumber
              ref={operatorsNumberRef}
              typeInput="normal"
              label={t('externalCompanies.operatorsNumber')}
              type={'number'}
              placeholder={'1'}
              defaultValue={'1'}
            />
            <MainButton type="button" text="⮕" onClick={addData} />
          </SubContainerFilters>
        </ContainerFilters>
      </SubContainer>
      <SubContainer>
        <Typography size="L" variant="semiBold">
          {t('externalCompanies.title')}
        </Typography>
        <StyledTableContainer>
          <Table>
            <Thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <Tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <ThStyled
                      key={header.id}
                      cell={header.id as cellExternalCompaniesTypes}
                    >
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                    </ThStyled>
                  ))}
                </Tr>
              ))}
            </Thead>
            <Tbody>
              {table.getRowModel().rows.map((row) => (
                <TRBody key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <TdStyled key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </TdStyled>
                  ))}
                </TRBody>
              ))}
            </Tbody>
          </Table>
        </StyledTableContainer>
      </SubContainer>
    </Container>
  );
};
